/**
 * Simplified LayoutManager — modeled on the-kitchen-console's version
 * but stripped of multi-tab center stage and saved-layout v3 schema
 * concerns. Single Gridstack canvas, one widget per CubeDef.
 *
 * Responsibilities:
 *   1. Build a GridStack inside the provided host on first mount.
 *   2. Add one widget per CubeDef at saved-geometry (or default).
 *   3. On every store update, re-render each cube's TemplateResult into
 *      its content div. lit-html is idempotent → cheap.
 *   4. Persist layout to localStorage on user drag/resize.
 */
import { render } from "lit-html";
import { GridStack, type GridStackOptions } from "gridstack";
import "gridstack/dist/gridstack.min.css";

import { CUBES, type CubeDef, type CubeGeometry } from "./cubes";
import { store } from "../data/store";

const STORAGE_KEY = "festival-ops.layout.v1";

interface SavedItem { id: string; x?: number; y?: number; w?: number; h?: number }

class LayoutManager {
  private grid: GridStack | null = null;
  private contentEls = new Map<string, HTMLElement>();

  mount(host: HTMLElement): void {
    host.classList.add("grid-stack-host");
    const stackEl = document.createElement("div");
    stackEl.className = "grid-stack";
    host.appendChild(stackEl);

    const opts: GridStackOptions = {
      column: 12,
      cellHeight: 36,
      // Gutter between cubes. Gridstack renders this by insetting
      // .grid-stack-item-content; the absolute visible gap between
      // two adjacent cube content boxes is roughly 2 × margin.
      // 12 → ~24px gap, matches the-kitchen-console's apparent gutter.
      margin: 12,
      float: true,
      animate: true,
      handle: ".cube-shell__drag",
      resizable: { handles: "se,sw,e,s,w" },
    };
    this.grid = GridStack.init(opts, stackEl);

    const saved = this.loadSaved();
    for (const cube of CUBES) {
      const geom = saved?.[cube.id] ?? cube.defaultGeometry;
      this.addWidget(cube, geom);
    }
    this.grid.on("change", () => this.persist());
  }

  /** Re-render every cube body against the current store state. */
  paintAll(): void {
    const state = store.state;
    for (const cube of CUBES) {
      const el = this.contentEls.get(cube.id);
      if (el) render(cube.render(state), el);
    }
  }

  /** Programmatic resize from a component (e.g. collapse the
   *  preview cube). cubeId matches a CubeDef.id. */
  resizeCube(cubeId: string, h: number): void {
    if (!this.grid) return;
    const widget = document.querySelector<HTMLElement>(`[gs-id="${cubeId}"]`);
    if (!widget) return;
    this.grid.update(widget, { h });
  }

  private addWidget(cube: CubeDef, geom: CubeGeometry): void {
    if (!this.grid) return;
    const widget = document.createElement("div");
    widget.classList.add("grid-stack-item");
    widget.setAttribute("gs-id", cube.id);
    widget.setAttribute("gs-x", String(geom.x));
    widget.setAttribute("gs-y", String(geom.y));
    widget.setAttribute("gs-w", String(geom.w));
    widget.setAttribute("gs-h", String(geom.h));
    // Per-cube locked sizing — preview is one. Gridstack reads these
    // gs- attributes and disables the matching resize handles.
    if (cube.noResize) widget.setAttribute("gs-no-resize", "true");
    if (cube.minW != null) widget.setAttribute("gs-min-w", String(cube.minW));
    if (cube.maxW != null) widget.setAttribute("gs-max-w", String(cube.maxW));
    if (cube.minH != null) widget.setAttribute("gs-min-h", String(cube.minH));
    if (cube.maxH != null) widget.setAttribute("gs-max-h", String(cube.maxH));

    const content = document.createElement("div");
    content.classList.add("grid-stack-item-content");
    widget.appendChild(content);

    this.grid.makeWidget(widget);
    this.contentEls.set(cube.id, content);
  }

  private loadSaved(): Record<string, CubeGeometry> | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SavedItem[];
      const map: Record<string, CubeGeometry> = {};
      for (const it of parsed) {
        if (typeof it.x === "number" && typeof it.y === "number"
            && typeof it.w === "number" && typeof it.h === "number") {
          map[it.id] = { x: it.x, y: it.y, w: it.w, h: it.h };
        }
      }
      return map;
    } catch {
      return null;
    }
  }

  private persist(): void {
    if (!this.grid) return;
    const items: SavedItem[] = [];
    this.grid.engine.nodes.forEach((n) => {
      if (n.id) items.push({ id: String(n.id), x: n.x, y: n.y, w: n.w, h: n.h });
    });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }
}

export const layoutManager = new LayoutManager();
